<script>import { createEventDispatcher, onMount } from "svelte";
import { t, Verbiage } from "@infinitro/content";
import { ErrorIcon, WarningIcon } from "@infinitro/icons";
import { stateUi } from "@infinitro/config";
const dispatch = createEventDispatcher();
export let value = "";
export let placeholder = $t(Verbiage.write_here);
export let disabled = false;
export let icon = null;
export let endIcon = null;
export let endIconProps = {};
export let inputmode = void 0;
export let customClasses = "";
export let cols = 1;
export let inputClasses = "";
export let iconClasses = "";
export let endIconClasses = "";
export let error = false;
export let warning = false;
export let message = "";
export let size = "large";
export let id = "";
export let type = "text";
export let name = "";
export let autocomplete = "off";
export let required = false;
export let readonly = false;
export let maxlength = void 0;
export let minlength = void 0;
export let pattern = void 0;
export let step = void 0;
export let title = void 0;
export let rows = 8;
export let element = null;
export let customStyles = "";
export let loading = false;
export let autoResizeTextarea = false;
export let customBorderRadius = void 0;
export let transparentBackground = false;
$: textareaContainerHeight = "96px";
function autoResizeInput() {
  if (element) {
    element.style.height = "37px";
    element.style.height = `${element.scrollHeight}px`;
    textareaContainerHeight = `${element.scrollHeight}px`;
  }
}
function handleInput(event) {
  if (autoResizeTextarea) {
    autoResizeInput();
  }
  dispatch("input", event.target.value);
}
function handleBlur(event) {
  dispatch("blur", event);
}
function handleFocus(event) {
  dispatch("focus", event);
}
onMount(() => {
  if (type === "textarea") {
    autoResizeInput();
  }
});
$: if (autoResizeTextarea && type === "textarea") {
  autoResizeInput();
}
$: useAlternative2 = $stateUi.input.inputStyle === "alternative2";
$: useTransparentBackground = $stateUi.input?.transparentBackground ?? false;
</script>

<div class="component-container">
	<div
		class={`input-container ${type === 'textarea' ? 'textarea-container' : ''} ${size} ${customClasses} ${disabled ? 'input-disabled' : ''} ${error ? 'input-error' : ''} ${warning ? 'input-warning' : ''} ${useAlternative2 ? 'input-alternative2' : ''} ${useTransparentBackground ? 'input-transparent' : ''}`}
		style={`
		${textareaContainerHeight !== '96px' ? `height:${textareaContainerHeight};` : ''}
		${customBorderRadius ? `--border-radius-input:${customBorderRadius};` : ''}
	`}
	>
		{#if error || warning}
			<div
				class={`background-layer ${error ? 'input-error' : ''} ${warning ? 'input-warning' : ''}`}
			></div>
		{/if}
		{#if icon && type !== 'textarea'}
			<div class={`input-icon ${size} ${iconClasses}`}>
				{#if icon && typeof icon === 'string'}
					<img src={icon} alt={$t(Verbiage.input_icon_alt, 'alts')} />
				{/if}
				{#if icon && typeof icon === 'function'}
					<svelte:component this={icon} />
				{/if}
			</div>
		{/if}
		<slot name="components"></slot>
		{#if type === 'text'}
			<input
				{id}
				type="text"
				{placeholder}
				bind:value
				class={`input ${inputClasses}`}
				{disabled}
				{name}
				{autocomplete}
				{required}
				{readonly}
				{maxlength}
				{minlength}
				{pattern}
				{step}
				{title}
				style={customStyles}
				on:input={handleInput}
				on:blur={handleBlur}
				on:focus={handleFocus}
				bind:this={element}
			/>
		{:else if type === 'email'}
			<input
				{id}
				type="email"
				{placeholder}
				bind:value
				class={`input ${inputClasses}`}
				{disabled}
				{name}
				{autocomplete}
				{required}
				{readonly}
				{maxlength}
				{minlength}
				{pattern}
				{step}
				{title}
				style={customStyles}
				on:input={handleInput}
				on:blur={handleBlur}
				on:focus={handleFocus}
				bind:this={element}
			/>
		{:else if type === 'password'}
			<input
				{id}
				type="password"
				{placeholder}
				bind:value
				class={`input ${inputClasses}`}
				{disabled}
				{name}
				{autocomplete}
				{required}
				{readonly}
				{maxlength}
				{minlength}
				{pattern}
				{step}
				{title}
				style={customStyles}
				on:input={handleInput}
				on:blur={handleBlur}
				on:focus={handleFocus}
				bind:this={element}
			/>
		{:else if type === 'number'}
			<input
				{id}
				type="number"
				{placeholder}
				bind:value
				class={`input ${inputClasses}`}
				{disabled}
				{name}
				{autocomplete}
				{required}
				{readonly}
				{maxlength}
				{minlength}
				{pattern}
				{step}
				{title}
				{inputmode}
				style={customStyles}
				on:input={handleInput}
				on:blur={handleBlur}
				on:focus={handleFocus}
				bind:this={element}
			/>
		{:else if type === 'textarea'}
			<textarea
				{id}
				{placeholder}
				bind:value
				class={`textarea ${inputClasses}`}
				{disabled}
				{name}
				{autocomplete}
				{required}
				{readonly}
				{maxlength}
				{minlength}
				{title}
				{rows}
				{cols}
				style={customStyles}
				on:input={handleInput}
				on:blur={handleBlur}
				on:focus={handleFocus}
				bind:this={element}
			></textarea>
		{/if}
		<slot name="components-end"></slot>
		{#if endIcon && type !== 'textarea'}
			<div class={`end-icon ${size} ${endIconClasses}`}>
				{#if typeof endIcon === 'string'}
					<img src={endIcon} alt={$t(Verbiage.input_icon_alt, 'alts')} class:loading />
				{:else if typeof endIcon === 'function'}
					<svelte:component this={endIcon} {...endIconProps} />
				{/if}
			</div>
		{/if}
	</div>
	{#if message && (error || warning)}
		<div
			class={`input-message ${error ? 'message-error' : ''} ${warning ? 'message-warning' : ''}`}
		>
			<span class="message-icon">
				{#if error}
					<ErrorIcon />
				{:else}
					<WarningIcon height={16} width={16} />
				{/if}
			</span>
			{message}
		</div>
	{/if}
</div>

<style>
	.component-container {
		display: flex;
		width: 100%;
		flex-direction: column;
}

	.input-container {
		position: relative;
		display: flex;
		align-items: center;
		border-radius: var(--border-radius-input);
		border-width: 1px;
		border-color: var(--component-input-border);
		background-color: var(--component-input-background);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
}

	.input-container:hover {
		border-color: var(--component-input-border-hover);
}

	.input-container:active {
		border-color: var(--component-input-border-active);
}

	.input-container:focus-within {
		border-color: var(--component-input-border-active);
		outline-color: var(--component-input-border-active);
	}

	.background-layer {
		position: absolute;
		inset: 0px;
		border-radius: var(--border-radius-input);
		background-color: var(--color-background-500);
}

	.input-container.input-disabled {
		opacity: 0.25;
		background: var(--gradient-alternative-4);
}

	.input-alternative2 {
		position: relative;
		border-radius: 8px;
		border-style: none;
		background: linear-gradient(
			180deg,
			rgba(var(--color-background-900-rgb) / 0.6) 0%,
			rgba(var(--color-background-900-rgb) / 0.5) 100%
		);
}

	.input-alternative2::before {
		position: absolute;
		inset: 0px;
		border-radius: 8px;
		padding: 1px;
		content: '';
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
}

	.input-container.input-alternative2.input-error::before,
	.input-container.input-alternative2.input-warning::before {
		background: none;
	}

	.input-container.input-alternative2.input-error::after,
	.input-container.input-alternative2.input-warning::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 8px;
		border: 1px solid transparent;
		pointer-events: none;
		z-index: 3;
	}

	.input-container.input-alternative2.input-error::after {
		border-color: var(--color-error-500);
	}

	.input-container.input-alternative2.input-warning::after {
		border-color: var(--color-warning-500);
	}

	.input-error {
		border-color: var(--color-error-500);
		background-image: none;
}

	.input-warning {
		border-color: var(--color-warning-500);
		background-image: none;
}

	.input-container .input-error {
		background-color: color-mix(in srgb,var(--color-error-500) 10%,transparent);
}

	.input-container .input-warning {
		background-color: color-mix(in srgb,var(--color-warning-500) 10%,transparent);
}

	@supports not (background: color-mix(in srgb, #000 10%, transparent)) {
		.input-container .input-error,
		.input-container .input-warning {
				position: relative;
		}

		.input-container .input-error::before,
		.input-container .input-warning::before {
			content: '';
			position: absolute;
			inset: 0px;
			border-radius: inherit;
			pointer-events: none;
		}

		.input-container .input-error::before {
			background: var(--color-error-500);
			opacity: 0.1; /* ~10% */
		}

		.input-container .input-warning::before {
			background: var(--color-warning-500);
			opacity: 0.1; /* ~10% */
		}
	}

	.input-icon,
	.end-icon {
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: center;
}

	.input-icon img,
	.end-icon img {
		height: 20px;
}

	.input-icon.small img,
	.end-icon.small img {
		height: 16px;
}

	.input-icon {
		margin-right: -8px;
		padding-left: 0.75rem;
}

	.end-icon {
		margin-left: -8px;
		padding-right: 0.75rem;
}

	.input,
	.textarea {
		position: relative;
		height: 100%;
		max-height: 90px;
		min-height: 37px;
		width: 100%;
		background-color: transparent;
		padding-left: 15px;
		padding-right: 15px;
		padding-top: 19px;
		padding-bottom: 19px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
		border-radius: inherit;
}

	.input:focus,
	.textarea:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	/* Keep browser autofill aligned with container shape and theme. */
	.input:-webkit-autofill {
		-webkit-text-fill-color: var(--color-text-100) !important;
		box-shadow: 0 0 0 1000px var(--color-background-900) inset;
		caret-color: var(--color-text-100);
		border-radius: inherit;
	}
	.input:-webkit-autofill,
	.input:-webkit-autofill:hover,
	.input:-webkit-autofill:focus,
	.input:autofill {
		-webkit-text-fill-color: var(--color-text-100) !important;
		box-shadow: 0 0 0 1000px var(--color-background-900) inset;
		caret-color: var(--color-text-100);
		border-radius: inherit;
	}

	.textarea {
		resize: none;
}

	.input-message {
		position: relative;
		z-index: 40;
		margin: 0.75rem;
		margin-top: 0.5rem;
		display: flex;
		align-items: center;
		border-radius: var(--border-radius-input-message);
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		font-size: 0.6rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity));
}

	.message-error {
		background-color: var(--color-error-900);
}

	.message-warning {
		background-color: var(--color-warning-900);
}

	.message-icon {
		margin-right: 0.25rem;
		height: 1rem;
		width: 1rem;
}

	/* Size classes */
	.input-container.large {
		height: 48px;
		font-size: 16px;
}

	.input-container.medium {
		height: 40px;
		font-size: 16px;
}

	.input-container.small {
		height: 36px;
		font-size: 16px;
}

	.textarea-container {
		max-height: 96px;
		min-height: 37px;
}

	input[type='number']::-webkit-inner-spin-button,
	input[type='number']::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	input[type='number'] {
		-moz-appearance: textfield;
	}

	.loading {
		transform-origin: center;
}

	@keyframes spin {

		to {
				transform: rotate(360deg);
		}
}

	.loading {
		animation: spin 1s linear infinite;
}

	.textarea::-webkit-scrollbar {
		width: 0.25rem;
}

	.textarea::-webkit-scrollbar-track {
		background-color: transparent;
}

	.textarea::-webkit-scrollbar-thumb {
		border-radius: 9999px;
		background-color: var(--color-secondary-500);
}

	.textarea::-webkit-scrollbar-thumb:hover {
		background-color: var(--color-neutral-100);
}

	.input-no-bg {
		background: transparent !important;
	}

	.input-no-bg .background-layer {
		display: none;
	}
	.input-transparent:is(:hover, :active) {
		background: var(--gradient-main);
	}

	.input-transparent :is(.input, .textarea) {
		background: transparent;
	}

	.input-transparent
		.input:is(:-webkit-autofill, :-webkit-autofill:hover, :-webkit-autofill:focus, :-webkit-autofill) {
		background: transparent !important;
		box-shadow: 0 0 0 1000px transparent inset !important;
		-webkit-text-fill-color: var(--color-text-100) !important;
		caret-color: var(--color-text-100);
		-webkit-transition: background-color 9999s ease-out 0s;
		transition: background-color 9999s ease-out 0s;
	}

	.input-transparent:is(:hover, :active) :is(.input, .textarea),
	.input-transparent
		.input:is(:-webkit-autofill, :-webkit-autofill:hover, :-webkit-autofill:focus, :autofill) {
		background: transparent !important;
		box-shadow: 0 0 0 1000px transparent inset !important;
		-webkit-text-fill-color: var(--color-text-100) !important;
		caret-color: var(--color-text-100);
		-webkit-transition: background-color 9999s ease-out 0s;
		transition: background-color 9999s ease-out 0s;
	}
</style>
